import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2, Sparkles, Trash2, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { useChatUI } from "@contexts/ChatContext";
import { useChat } from "../hooks/useChat";

const SEVERITY_META = {
  risk: { icon: AlertTriangle, color: "#ef4444" },
  warn: { icon: AlertCircle, color: "#f59e0b" },
  info: { icon: Info, color: "#3b82f6" },
};

/**
 * Right-side chat drawer. Pushes content (sticky flex item), draggable
 * left edge, auto-growing textarea, always-mounted so history survives
 * navigation.
 *
 * When user taps "!" / Discuss / selects a container, the chat opens
 * with a TOPIC set — it renders as a banner at the top of the thread.
 * The topic text is silently prepended to outgoing LLM context so the
 * assistant responds with full awareness. User's input stays empty.
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function ChatDrawer() {
  const {
    open,
    width,
    setWidth,
    closeChat,
    consumeSeed,
  } = useChatUI();
  const { messages, sending, error, topic, setTopic, send, clear } = useChat();
  const [draft, setDraft] = useState("");
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartW = useRef(width);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const isMobile = useIsMobile();

  // Consume any pending topic whenever the drawer opens. Replaces the
  // previously-pinned topic (most recent "Discuss" wins).
  useEffect(() => {
    if (!open) return;
    const pending = consumeSeed();
    if (pending?.topic) {
      setTopic(pending.topic);
    }
    setTimeout(() => textareaRef.current?.focus(), 80);
  }, [open, consumeSeed, setTopic]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, topic]);

  const onDragStart = useCallback(
    (e) => {
      e.preventDefault();
      dragging.current = true;
      dragStartX.current = e.clientX || e.touches?.[0]?.clientX || 0;
      dragStartW.current = width;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.clientX || e.touches?.[0]?.clientX || 0;
      const delta = dragStartX.current - x;
      setWidth(dragStartW.current + delta);
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [setWidth]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const maxHeight = 8 * 20;
    ta.style.height = Math.min(ta.scrollHeight, maxHeight) + "px";
  }, [draft]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    const content = draft.trim();
    if (!content || sending) return;
    setDraft("");
    await send(content);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  // Lock body scroll on mobile while drawer is open
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <aside
      className={
        isMobile
          ? "fixed inset-0 z-[90] flex flex-col"
          : "sticky top-0 self-start h-screen flex flex-col flex-shrink-0 relative"
      }
      style={{
        background: "var(--bg-root)",
        borderLeft: "1px solid var(--border-card)",
        ...(isMobile ? {} : { width: `${width}px` }),
      }}
    >
      {/* Drag handle (desktop only) */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        className="hidden md:block absolute top-0 left-0 w-1 h-full cursor-col-resize group z-20"
        style={{ transform: "translateX(-50%)" }}
        aria-label="Resize chat"
        title="Drag to resize"
      >
        <div className="absolute -left-1.5 top-0 w-4 h-full" />
        <div className="absolute left-0 top-0 w-full h-full transition-colors" style={{ background: "var(--border-card)" }} />
        <div className="absolute left-0 top-0 w-full h-full opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity" style={{ background: "rgba(79, 70, 229, 0.5)" }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1 pointer-events-none opacity-50 group-hover:opacity-100 transition-opacity">
          <span className="w-0.5 h-0.5 rounded-full" style={{ background: "var(--text-secondary)" }} />
          <span className="w-0.5 h-0.5 rounded-full" style={{ background: "var(--text-secondary)" }} />
          <span className="w-0.5 h-0.5 rounded-full" style={{ background: "var(--text-secondary)" }} />
          <span className="w-0.5 h-0.5 rounded-full" style={{ background: "var(--text-secondary)" }} />
          <span className="w-0.5 h-0.5 rounded-full" style={{ background: "var(--text-secondary)" }} />
        </div>
      </div>

      {/* Header */}
      <header
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: "1px solid var(--border-card)" }}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: "var(--ai-icon)" }} />
          <h2 className="text-sm font-semibold" style={{ color: "var(--text-heading)" }}>
            Project Assistant
          </h2>
        </div>
        <div className="flex items-center gap-1">
          {(messages.length > 0 || topic) && (
            <button
              onClick={clear}
              className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
              style={{ color: "var(--text-secondary)" }}
              title="Clear chat"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={closeChat}
            className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Thread */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto p-3 space-y-2"
        style={{ background: "var(--bg-root)" }}
      >
        {messages.length === 0 && !sending && !topic && (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3 p-6">
            <MessageSquare className="w-8 h-8" style={{ color: "var(--text-secondary)" }} />
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: "var(--text-heading)" }}>
                Ask questions about your project
              </p>
              <p className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                Tap a <span className="font-semibold">!</span> indicator anywhere in the app to discuss a specific item, or type a free-form question below.
              </p>
              <p className="text-[11px] leading-relaxed mt-2" style={{ color: "var(--text-secondary)" }}>
                For a full project review, click <span className="font-semibold">Review my project</span> on the Dashboard.
              </p>
            </div>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}

        {sending && (
          <div className="flex items-center gap-1.5 text-[11px] px-2" style={{ color: "var(--text-secondary)" }}>
            <Loader2 className="w-3 h-3 animate-spin" />
            Thinking…
          </div>
        )}

        {error && (
          <div
            className="px-3 py-2 rounded-md text-[11px]"
            style={{
              background: "rgba(239,68,68,0.12)",
              color: "#ef4444",
              border: "1px solid rgba(239,68,68,0.3)",
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Topic banner — pinned just above composer */}
      {topic && (
        <div className="px-3 pt-2 flex-shrink-0">
          <TopicBanner topic={topic} onClear={() => setTopic(null)} />
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={onSubmit}
        className="flex items-end gap-2 p-3 flex-shrink-0"
        style={{ borderTop: topic ? "none" : "1px solid var(--border-card)" }}
      >
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type your question…"
          rows={1}
          className="flex-1 resize-none px-3 py-2 text-xs rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
          style={{
            background: "var(--bg-input)",
            border: "1px solid var(--border-input)",
            color: "var(--text-heading)",
            minHeight: "34px",
            lineHeight: "18px",
          }}
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          className="p-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 transition-colors"
          aria-label="Send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </aside>
  );
}

function TopicBanner({ topic, onClear }) {
  const sev = topic.severity ? SEVERITY_META[topic.severity] : null;
  const Icon = sev?.icon;
  return (
    <div
      className="rounded-lg p-2 flex items-start gap-2"
      style={{
        background: "var(--bg-card-frosted)",
        border: `1px solid ${sev?.color ?? "var(--accent-text)"}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
      }}
    >
      {Icon ? (
        <div
          className="p-1 rounded flex-shrink-0 mt-0.5"
          style={{ background: `${sev.color}22`, color: sev.color }}
        >
          <Icon className="w-3 h-3" />
        </div>
      ) : (
        <Sparkles className="w-3 h-3 flex-shrink-0 mt-1" style={{ color: "var(--ai-icon)" }} />
      )}
      <div className="flex-1 min-w-0">
        <p
          className="text-[9px] uppercase tracking-wider font-semibold mb-0.5"
          style={{ color: "var(--text-secondary)" }}
        >
          Asking about{topic.containerLabel ? ` — ${topic.containerLabel}` : ""}
        </p>
        <p className="text-xs font-semibold mb-0.5" style={{ color: "var(--text-heading)" }}>
          {topic.title}
        </p>
        {topic.body && (
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-body)" }}>
            {topic.body}
          </p>
        )}
      </div>
      <button
        onClick={onClear}
        className="p-0.5 rounded hover:bg-black/5 flex-shrink-0"
        style={{ color: "var(--text-secondary)" }}
        aria-label="Clear topic"
        title="Clear topic"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className="max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs leading-relaxed"
        style={
          isUser
            ? {
                background: "var(--accent-bg)",
                color: "var(--text-heading)",
                border: "1px solid var(--border-input)",
              }
            : {
                background: "var(--bg-card)",
                color: "var(--text-body)",
                border: "1px solid var(--border-card)",
              }
        }
      >
        <div className="whitespace-pre-wrap break-words">{message.content}</div>
      </div>
    </div>
  );
}
